import React from "react";
import SelectMenu from "../../../controls/SelectMenu";
import NextInProcessModal from "../../../inProcessMonitoring/NextInProcessModal";
import Button from "../../../ui/Button";
import {
  convertToDefEventPara,
  isFeatureFlag,
  productionStatusOptions,
} from "../../../../shared/utility";

interface ManufacturingRecipeSelectProps {
  isEdit?: boolean;
  manufacturedProduct: any;
  onFetchRecipeItems: (recipeId: any, weight: any) => void;
  recipes: any[];
  handleInputChange: (event: any) => void;
  values: any;
  inProcessMonitoringHistory: any;
  updateInProcessMonitoringHistory: (payload: any) => Promise<any>;
  setEmptyModelOpen: (modal: any) => void;
  previousInProcessMonitoringHistory: (payload: any) => Promise<any>;
}

export default function ManufacturingRecipeSelect(
  props: ManufacturingRecipeSelectProps,
) {
  const {
    isEdit,
    manufacturedProduct,
    onFetchRecipeItems,
    recipes,
    handleInputChange,
    values,
    inProcessMonitoringHistory,
    updateInProcessMonitoringHistory,
    setEmptyModelOpen,
    previousInProcessMonitoringHistory,
  } = props;

  const recipeId = values.recipe_id ?? null;
  const chosenRecipe = recipeId
    ? recipes.find((el) => el.value === recipeId)
    : null;

  const currentStatus = manufacturedProduct?.status
    ? productionStatusOptions.find(
        (option) => option.value === String(manufacturedProduct.status),
      )
    : null;

  const showInProcessMonitoringUi =
    isFeatureFlag("isQa") &&
    !!inProcessMonitoringHistory?.in_process_status_id &&
    !!inProcessMonitoringHistory?.in_process_status_name;

  const handleMoveToNext = async () => {
    if (!inProcessMonitoringHistory?.next_process_status_id) return;

    const confirmAction = async (entry: any, credentials: any) => {
      setEmptyModelOpen({ childComponent: null, open: false });
      await updateInProcessMonitoringHistory({
        product_id: values.product_id,
        production_id: entry.production_id,
        current_history_id: entry.id,
        next_in_process_status_id: entry.next_process_status_id,
        username: credentials.username,
        password: credentials.password,
      });
    };

    setEmptyModelOpen({
      open: true,
      childComponent: (
        <NextInProcessModal
          buttonMessage="Move"
          confirmAction={(credentials: any) =>
            confirmAction(inProcessMonitoringHistory, credentials)
          }
          processRecord={inProcessMonitoringHistory}
          message={`Move ${
            inProcessMonitoringHistory.description_en
              ? inProcessMonitoringHistory.description_en
              : "this product"
          } to next status: ${
            inProcessMonitoringHistory.next_process_status_name
          }?`}
          setOpen={setEmptyModelOpen}
          title="Move to next status"
        />
      ),
    });
  };

  const handleMoveToPrevious = async () => {
    if (!inProcessMonitoringHistory?.previous_process_status_id) return;

    const confirmAction = async (entry: any, credentials: any) => {
      setEmptyModelOpen({ childComponent: null, open: false });

      await previousInProcessMonitoringHistory({
        product_id: values.product_id,
        production_id: entry.production_id,
        current_history_id: entry.id,
        previous_process_status_id: entry.previous_process_status_id,
        username: credentials.username,
        password: credentials.password,
      });
    };

    setEmptyModelOpen({
      open: true,
      childComponent: (
        <NextInProcessModal
          buttonMessage="Move"
          confirmAction={(credentials: any) =>
            confirmAction(inProcessMonitoringHistory, credentials)
          }
          processRecord={inProcessMonitoringHistory}
          message={`Move ${
            inProcessMonitoringHistory.description_en
              ? inProcessMonitoringHistory.description_en
              : "this product"
          } to previous status: ${
            inProcessMonitoringHistory.previous_process_status_name
          }?`}
          setOpen={setEmptyModelOpen}
          title="Move to previous status"
        />
      ),
    });
  };

  return (
    <div className="p-4 card-custom">
      <div className="grid grid-cols-1 gap-2">
        <div className="col-span-1">
          <SelectMenu
            handleInputChange={(e: any) => {
              onFetchRecipeItems(e.target.value.value, values.new_weight);
              handleInputChange(
                convertToDefEventPara("selected_recipe", e.target.value.value),
              );
            }}
            name="selected_recipe"
            options={recipes}
            defaultValue={isEdit ? chosenRecipe : null}
            title={"Recipe"}
            value={values.selected_recipe ?? values.recipe_id}
          />
        </div>
        <div className="col-span-1">
          {showInProcessMonitoringUi ? (
            <div className="bg-white rounded-md p-3 text-base">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <div className="text-sm text-gray-600 font-medium">
                    Current Status
                  </div>
                  <div className="mt-1">
                    <span className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-lg text-black shadow-sm">
                      {inProcessMonitoringHistory.in_process_status_name}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 font-medium">
                    Status Actions
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {inProcessMonitoringHistory.next_process_status_id && (
                      <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => handleMoveToNext()}
                        style={
                          inProcessMonitoringHistory.next_process_status_color
                            ? {
                                backgroundColor:
                                  "#" +
                                  inProcessMonitoringHistory.next_process_status_color,
                              }
                            : undefined
                        }
                      >
                        Next Status:{" "}
                        {inProcessMonitoringHistory.next_process_status_name ||
                          "N/A"}
                      </Button>
                    )}

                    {inProcessMonitoringHistory.previous_process_status_id && (
                      <Button
                        type="button"
                        variant="danger"
                        size="md"
                        onClick={() => handleMoveToPrevious()}
                        disabled={
                          !inProcessMonitoringHistory.previous_process_status_id
                        }
                      >
                        Previous Status:{" "}
                        {inProcessMonitoringHistory.previous_process_status_name ||
                          "N/A"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <SelectMenu
              name="status"
              handleInputChange={(e: any) => {
                handleInputChange(
                  convertToDefEventPara("status", String(e.target.value.value)),
                );
              }}
              options={productionStatusOptions}
              defaultValue={isEdit ? currentStatus : null}
              title={"Status"}
              value={values.status}
            />
          )}
        </div>
      </div>
    </div>
  );
}
